<template>
  <div class="eye-chart">
    <div class="filter-container">
      <div class="toolbar-panel">
        <div class="toolbar-filters">
          <div class="toolbar-row toolbar-row--primary">
            <div class="toolbar-field">
              <label class="toolbar-label" for="user-id-input">用户ID:</label>
              <el-input
                v-model="userIdInput"
                class="toolbar-control"
                placeholder="请输入用户ID"
                id="user-id-input"
                size="mini"
                clearable
                inputmode="numeric"
                @input="handleUserIdInput">
              </el-input>
            </div>
            <div class="toolbar-field">
              <label class="toolbar-label" for="name-input">姓名:</label>
              <el-input
                v-model="selectedName"
                class="toolbar-control"
                placeholder="请输入姓名"
                prefix-icon="el-icon-search"
                id="name-input"
                size="mini"
                clearable>
              </el-input>
            </div>
            <div class="toolbar-field">
              <label class="toolbar-label" for="store-select">门店:</label>
              <el-select
                v-model="selectedStore"
                class="toolbar-control"
                placeholder="选择门店"
                id="store-select"
                size="mini"
                @change="handleStoreChange"
                clearable>
                <el-option v-for="store in stores" :key="store" :label="store" :value="store"></el-option>
              </el-select>
            </div>
            <div class="toolbar-field">
              <label class="toolbar-label" for="optometrist-select">验光师:</label>
              <el-select
                v-model="selectedOptometrist"
                class="toolbar-control"
                placeholder="选择验光师"
                id="optometrist-select"
                size="mini"
                @change="handleOptometristChange"
                clearable>
                <el-option v-for="optometrist in optometrists" :key="optometrist" :label="optometrist" :value="optometrist"></el-option>
              </el-select>
            </div>
            <div class="toolbar-field">
              <label class="toolbar-label" for="check-date-select">检查日期:</label>
              <el-select
                v-model="selectedCheckDate"
                class="toolbar-control"
                placeholder="选择检查日期"
                id="check-date-select"
                size="mini"
                @change="handleCheckDateChange"
                clearable>
                <el-option v-for="date in checkDates" :key="date" :label="date" :value="date"></el-option>
              </el-select>
            </div>
          </div>
          <div class="toolbar-row toolbar-row--secondary">
            <div class="toolbar-field">
              <label class="toolbar-label" for="outdoor-select">户外活动日期:</label>
              <el-select
                v-model="selectedOutdoorTime"
                class="toolbar-control"
                :placeholder="outdoorPlaceholder"
                id="outdoor-select"
                size="mini"
                @change="handleOutdoorTimeChange"
                clearable>
                <el-option v-for="time in outdoorTimes" :key="time" :label="time" :value="time"></el-option>
              </el-select>
            </div>
            <div class="toolbar-field">
              <label class="toolbar-label" for="model-select">AI模型:</label>
              <el-select
                v-model="selectedModel"
                class="toolbar-control"
                placeholder="选择AI模型"
                id="model-select"
                size="mini">
                <el-option label="Qwen-plus" value="Qwen-plus"></el-option>
                <el-option label="DeepSeek-V3" value="DeepSeek-V3"></el-option>
                <el-option label="Doubao-Seed-1.6" value="Doubao-Seed-1.6"></el-option>
              </el-select>
            </div>
            <div class="toolbar-field toolbar-field--wide">
              <label class="toolbar-label" for="export-date-range">导出日期:</label>
              <el-date-picker
                v-model="exportDateRange"
                class="toolbar-control"
                id="export-date-range"
                type="daterange"
                range-separator="至"
                start-placeholder="导出开始日期"
                end-placeholder="导出结束日期"
                size="mini"
                value-format="yyyy-MM-dd"
                :disabled="isExporting || isStreamAnalyzing">
              </el-date-picker>
            </div>
          </div>
        </div>
        <div class="toolbar-actions">
          <el-button
            plain
            size="mini"
            type="success"
            icon="el-icon-document-add"
            class="toolbar-button"
            @click="handleAddClick">
            新增记录
          </el-button>
          <el-button
            size="mini"
            type="primary"
            :icon="isStreamAnalyzing ? 'el-icon-loading' : 'el-icon-video-play'"
            class="toolbar-button"
            @click="handleAIStreamAnalysis"
            :loading="isStreamAnalyzing"
            :disabled="isAnalyzing || isStreamAnalyzing">
            {{ isStreamAnalyzing ? '分析中' : 'AI分析' }}
          </el-button>
          <el-button
            plain
            size="mini"
            type="success"
            icon="el-icon-download"
            class="toolbar-button"
            @click="handleExportAiSuggestions"
            :loading="isExporting"
            :disabled="isStreamAnalyzing || isExporting">
            {{ isExporting ? '导出中' : '导出产品建议' }}
          </el-button>
          <el-dropdown
            class="toolbar-dropdown"
            trigger="click"
            @command="handleToolbarCommand"
            :disabled="isStreamAnalyzing">
            <el-button plain size="mini" type="info" class="toolbar-button">
              更多操作<i class="el-icon-arrow-down el-icon--right"></i>
            </el-button>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item command="print"><i class="el-icon-printer"></i>打印报告</el-dropdown-item>
              <el-dropdown-item command="annotation"><i class="el-icon-edit-outline"></i>分析批注</el-dropdown-item>
              <el-dropdown-item command="cache"><i class="el-icon-collection"></i>查看AI缓存</el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
          <el-button
            ref="hiddenPrintButton"
            plain
            size="mini"
            type="info"
            icon="el-icon-printer"
            class="toolbar-hidden-print"
            v-print="printConfig"
            tabindex="-1"
            :disabled="isStreamAnalyzing">
            打印报告
          </el-button>
        </div>
      </div>
    </div>
    <ReusableTable ref="reusableTable" name="check" :tableData="filteredTableData.check || []" :tableColumns="tableColumns.check" :pageSize="4" @current-row-change="handleCurrentRowChange" @row-click="handleRowClick" @delete-row="handleDeleteRow" @update-row="handleUpdateRow"/>
    
    <!-- 分析批注弹窗 -->
    <el-dialog
      title="分析批注"
      :visible.sync="annotationDialogVisible"
      width="80%"
      :close-on-click-modal="false"
      @close="handleAnnotationDialogClose">
      <div class="annotation-content">
        <div class="annotation-input-section">
          <label for="annotation-input" style="font-size: 14px; margin-bottom: 8px; display: block;">批注内容:</label>
          <el-input
            id="annotation-input"
            type="textarea"
            :rows="4"
            placeholder="请输入您的批注内容..."
            v-model="annotationInput"
            resize="vertical">
          </el-input>
        </div>
        
        <div class="annotation-ai-section">
          <label style="font-size: 14px; margin-bottom: 8px; display: block;">当前AI分析结果:</label>
          <div class="ai-analysis-area">
            <EyeAI :value="annotationAIResult"/>
          </div>
        </div>
      </div>
      
      <span slot="footer" class="dialog-footer">
        <el-button @click="annotationDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleAnnotationSubmit" :loading="isAnnotationSubmitting">
          {{ isAnnotationSubmitting ? '提交中...' : '提交' }}
        </el-button>
      </span>
    </el-dialog>

    <AiCacheDialog :visible.sync="aiCacheDialogVisible" />
  </div>
</template>

<script>
import ReusableTable from './ReusableTable.vue';
import EyeAI from './EyeAI.vue';
import AiCacheDialog from './AiCacheDialog.vue';

export default {
  name: 'EyeChart',
  components: {
    ReusableTable,
    EyeAI,
    AiCacheDialog
  },
  props: {
    onRowClick: {
      type: Function,
      default: null
    }
  },
  data() {
    return {
      // 分析批注相关数据
      annotationDialogVisible: false,
      annotationInput: '',
      annotationAIResult: '<div style="text-align: center; color: #909399;"><p>请输入批注内容后点击提交，AI将为您生成分析结果</p></div>',
      isAnnotationSubmitting: false,
      aiCacheDialogVisible: false,
      
      printConfig: {
        id: '#printArea',
        popTitle: '猫头鹰AI视光师助理',
        extraHead: '<meta charset="UTF-8">',
        preview: false,
        previewTitle: '猫头鹰AI视光师助理',
        previewBeforeOpenCallback: () => {},
        previewOpenCallback: () => {},
        beforeOpenCallback: () => {},
        openCallback: () => {},
        closeCallback: () => {}
      },
      analysisResult: '',
      activeName: 'check',
      isAnalyzing: false,
      isStreamAnalyzing: false,
      streamEventSource: null,
      streamCompleted: false,
      streamReceivedContent: false,
      streamCloseExpected: false,
      testApiError: '',
      selectedUserId: null,
      userIdInput: '',
      selectedName: null,
      selectedOutdoorTime: null,
      selectedModel: 'Qwen-plus',
      exportDateRange: [],
      isExporting: false,
      exportPollTimer: null,
      selectedStore: null,
      selectedOptometrist: null,
      selectedCheckDate: null,
      currentSelectedRow: null,
      userIds: [],
      userNameSet: [],
      stores: [],
      optometrists: [],
      checkDates: [],
      allTableData: {},
      tableData: {
        check: [
          {"user_id": 1, "name": "李明", "age": 12, "gender": 0, "a1": 4.7, "a2": 5.0, "a3": 0.0, "a4": 0.0, "a5": 23.92, "a6": 23.65, 'outdoor': []},
          {"user_id": 2, "name": "里斯", "age": 12, "gender": 0, "a1": 4.7, "a2": 5.0, "a3": 0.0, "a4": 0.0, "a5": 23.92, "a6": 23.65, 'outdoor': []},
          {"user_id": 3, "name": "张宇轩", "age": 12, "gender": 0, "a1": 4.7, "a2": 5.0, "a3": 0.0, "a4": 0.0, "a5": 23.92, "a6": 23.65, 'outdoor': []},
          {"user_id": 4, "name": "刘欣怡", "age": 12, "gender": 1, "a1": 4.7, "a2": 5.0, "a3": 0.0, "a4": 0.0, "a5": 23.92, "a6": 23.65, 'outdoor': []},
          {"user_id": 5, "name": "陈浩然", "age": 12, "gender": 0, "a1": 4.7, "a2": 5.0, "a3": 0.0, "a4": 0.0, "a5": 23.92, "a6": 23.65, 'outdoor': []},
          {"user_id": 6, "name": "杨雅涵", "age": 12, "gender": 1, "a1": 4.7, "a2": 5.0, "a3": 0.0, "a4": 0.0, "a5": 23.92, "a6": 23.65, 'outdoor': []},
          {"user_id": 7, "name": "黄伟杰", "age": 12, "gender": 0, "a1": 4.7, "a2": 5.0, "a3": 0.0, "a4": 0.0, "a5": 23.92, "a6": 23.65, 'outdoor': []},
          {"user_id": 8, "name": "赵梦瑶", "age": 12, "gender": 1, "a1": 4.7, "a2": 5.0, "a3": 0.0, "a4": 0.0, "a5": 23.92, "a6": 23.65, 'outdoor': []},
          {"user_id": 9, "name": "何毅", "age": 12, "gender": 0, "a1": 4.7, "a2": 5.0, "a3": 0.0, "a4": 0.0, "a5": 23.92, "a6": 23.65, 'outdoor': []},
        ],
      },
      tableColumns: {
        check: [
          { prop: 'patient_no', label: '检查编号', defaultName: '201509300061', minWidth: '140' },
          { prop: 'user_id', label: '用户ID', defaultName: '6666', minWidth: '100' },
          { prop: 'store', label: '门店', defaultName: '庐阳区海棠店', minWidth: '150' },
          { prop: 'optometrist', label: '验光师', defaultName: '张三', minWidth: '120' },
          { prop: 'check_date', label: '检查日期', defaultName: '2025-01-01', minWidth: '120' },
          { prop: 'ai_first_analysis_date', label: '首次使用AI分析时间', defaultName: '2026-04-17 10:30', minWidth: '170' },
          { prop: 'name', label: '姓名', defaultName: '张三', minWidth: '100' },
          { prop: 'birth_date', label: '出生日期', defaultName: '12', minWidth: '150' },
          { prop: 'gender', label: '性别', defaultName: '男', minWidth: '80' },
          { prop: 'a16', label: '裸眼视力-右眼', defaultName: '4.9', minWidth: '140' },
          { prop: 'a17', label: '裸眼视力-左眼', defaultName: '4.9', minWidth: '140' },
          { prop: 'a18', label: '电脑验光-右眼', defaultName: '4.9', minWidth: '200' },
          { prop: 'a19', label: '电脑验光-左眼', defaultName: '4.9', minWidth: '200' },
          { prop: 'a20', label: '旧镜度数-右眼', defaultName: '-1.00/-1.00*16', minWidth: '200' },
          { prop: 'a21', label: '旧镜度数-左眼', defaultName: '-1.75/-0.00*170', minWidth: '200' },
          { prop: 'od_balanced_correct', label: '平衡屈光度矫正视力-右眼', defaultName: '5.0', minWidth: '190' },
          { prop: 'os_balanced_correct', label: '平衡屈光度矫正视力-左眼', defaultName: '5.0', minWidth: '190' },
          { prop: 'a2', label: '眼轴-右眼 (mm)', defaultName: '4.9', minWidth: '140' },
          { prop: 'a1', label: '眼轴-左眼 (mm)', defaultName: '5.0', minWidth: '140' },
          { prop: 'a4', label: '轴曲比-右眼', defaultName: '0.0', minWidth: '120' },
          { prop: 'a3', label: '轴曲比-左眼', defaultName: '0.0', minWidth: '120' },
          { prop: 'a6', label: '平衡屈光度-右眼 (D)', defaultName: '0.0', minWidth: '200' },
          { prop: 'a5', label: '平衡屈光度-左眼 (D)', defaultName: '0.0', minWidth: '200' },
          { prop: 'a7', label: '视功能AC/A', defaultName: '0.0', minWidth: '120' },
          { prop: 'a8', label: '视功能调节幅度-右眼', defaultName: '0.0', minWidth: '180' },
          { prop: 'a9', label: '视功能调节幅度-左眼', defaultName: '0.0', minWidth: '180' },
          { prop: 'a10', label: '远距离水平隐斜', defaultName: '0.0', minWidth: '140' },
          { prop: 'a11', label: '近距离水平隐斜', defaultName: '0.0', minWidth: '140' },
          { prop: 'a12', label: '远距离水平融像BI', defaultName: '0.0', minWidth: '180' },
          { prop: 'a13', label: '远距离水平融像BO', defaultName: '0.0', minWidth: '180' },
          { prop: 'a14', label: '近距离水平融像BI', defaultName: '0.0', minWidth: '180' },
          { prop: 'a15', label: '近距离水平融像BO', defaultName: '0.0', minWidth: '180' },
          { prop: 'a22', label: '立体视', defaultName: '无', minWidth: '120' },
          { prop: 'a23', label: 'Worth 4 dot', defaultName: '2', minWidth: '140' },
          { prop: 'a24', label: '正相对调节(PRA)', defaultName: '0.00', minWidth: '160' },
          { prop: 'a25', label: '负相对调节(NRA)', defaultName: '0.00', minWidth: '160' },
          { prop: 'a26', label: '调节反应(BCC)', defaultName: '0.00', minWidth: '160' },
          { prop: 'a27', label: '集合近点(NPC)', defaultName: '0.0', minWidth: '160' },
          { prop: 'a28', label: '调节灵敏度-双眼(OU)', defaultName: '0.0', minWidth: '180' },
          { prop: 'a29', label: '调节灵敏度-右眼(OD)', defaultName: '0.0', minWidth: '180' },
          { prop: 'a30', label: '调节灵敏度-左眼(OS)', defaultName: '0.0', minWidth: '180' },
          { prop: 'visual_function', label: '视功能检查结果评估', defaultName: '集合不足散开过度', minWidth: '220' },
          { prop: 'pre_diagnose_time', label: '上次检查日期', defaultName: '2025-08-15', minWidth: '160' },
        ],
      },
      currentPage: 1,
      currentRow: null,
      scores: {},
    };
  },
  computed: {
    filteredTableData() {
      console.log('Filtering!!!!');
      if (this.allTableData.check) {
        console.log('Filtering table data', this.allTableData.check.length);
      }
      if (!this.selectedUserId && !this.selectedName && !this.selectedStore && !this.selectedOptometrist && !this.selectedCheckDate) {
        const sorted = {};
        for (const key in this.allTableData) {
          sorted[key] = [...this.allTableData[key]].sort((a, b) => (b.user_id || 0) - (a.user_id || 0));
        }
        return sorted;
      }
      const filtered = {};
      for (const key in this.allTableData) {
        filtered[key] = this.allTableData[key]
          .filter(row => {
            const userIdMatch = !this.selectedUserId || row.user_id === this.selectedUserId;
            const nameMatch = !this.selectedName || (row.name && row.name.includes(this.selectedName));
            const storeMatch = !this.selectedStore || row.store === this.selectedStore;
            const optometristMatch = !this.selectedOptometrist || row.optometrist === this.selectedOptometrist;
            const checkDateMatch = !this.selectedCheckDate || row.check_date === this.selectedCheckDate;
            
            return userIdMatch && nameMatch && storeMatch && optometristMatch && checkDateMatch;
          })
          .sort((a, b) => (b.user_id || 0) - (a.user_id || 0));
      }
      return filtered;
    },
    outdoorTimes() {
      if (!this.currentSelectedRow || !this.currentSelectedRow.outdoor || !Array.isArray(this.currentSelectedRow.outdoor)) {
        return [];
      }
      return this.currentSelectedRow.outdoor.map(item => item.check_date).filter(data => data);
    },
    outdoorPlaceholder() {
      return this.currentSelectedRow ? '请选择户外活动日期' : '请选择一条数据';
    }
  },
  mounted() {
    this.initializeTableData();
  },
  methods: {
    initializeTableData() {
      console.log('initializeTableData');
      this.handleResetClick()
    },
    initializeUserIds() {
      const userIdSet = new Set();
      if (this.allTableData) {
        for (const tableKey in this.allTableData) {
          if (this.allTableData[tableKey] && Array.isArray(this.allTableData[tableKey])) {
            this.allTableData[tableKey].forEach(row => {
              if (row.user_id) {
                userIdSet.add(row.user_id);
              }
            });
          }
        }
      }
      console.log('initializeUserIds:', userIdSet);
      this.userIds = Array.from(userIdSet).sort((a, b) => a - b);
      console.log('Initialized user IDs:', this.userIds);
    },
    initializeUserNames() {
      const userNameSet = new Set();
      if (this.allTableData) {
        for (const tableKey in this.allTableData) {
          if (this.allTableData[tableKey] && Array.isArray(this.allTableData[tableKey])) {
            this.allTableData[tableKey].forEach(row => {
              if (row.user_id) {
                userNameSet.add(row.name);
              }
            });
          }
        }
      }
      console.log('initializeUserNames:', userNameSet);
      this.userNameSet = Array.from(userNameSet).sort();
      console.log('Initialized user Names:', this.userNameSet);
    },
    initializeStores() {
      const storeSet = new Set();
      if (this.allTableData) {
        for (const tableKey in this.allTableData) {
          if (this.allTableData[tableKey] && Array.isArray(this.allTableData[tableKey])) {
            this.allTableData[tableKey].forEach(row => {
              if (row.store && row.store !== null) {
                storeSet.add(row.store);
              }
            });
          }
        }
      }
      this.stores = Array.from(storeSet).sort();
      console.log('Initialized stores:', this.stores);
    },
    initializeOptometrists() {
      const optometristSet = new Set();
      if (this.allTableData) {
        for (const tableKey in this.allTableData) {
          if (this.allTableData[tableKey] && Array.isArray(this.allTableData[tableKey])) {
            this.allTableData[tableKey].forEach(row => {
              if (row.optometrist && row.optometrist !== null) {
                optometristSet.add(row.optometrist);
              }
            });
          }
        }
      }
      this.optometrists = Array.from(optometristSet).sort();
      console.log('Initialized optometrists:', this.optometrists);
    },
    initializeCheckDates() {
      const checkDateSet = new Set();
      if (this.allTableData) {
        for (const tableKey in this.allTableData) {
          if (this.allTableData[tableKey] && Array.isArray(this.allTableData[tableKey])) {
            this.allTableData[tableKey].forEach(row => {
              if (row.check_date && row.check_date !== null) {
                checkDateSet.add(row.check_date);
              }
            });
          }
        }
      }
      this.checkDates = Array.from(checkDateSet).sort((a, b) => b.localeCompare(a));
      console.log('Initialized check dates:', this.checkDates);
    },
    handleUserIdInput(value) {
      const sanitizedValue = (value || '').toString().replace(/\D+/g, '');
      this.userIdInput = sanitizedValue;
      const parsedId = sanitizedValue ? Number(sanitizedValue) : null;
      this.handleUserChange(parsedId);
    },
    handleUserChange(selectedId) {
      console.log('handleUserChange:', selectedId);
      this.selectedUserId = selectedId;
      const normalizedInput = selectedId !== null && selectedId !== undefined ? String(selectedId) : '';
      if (this.userIdInput !== normalizedInput) {
        this.userIdInput = normalizedInput;
      }
      this.currentRow = null;
      this.currentSelectedRow = null;
      this.selectedOutdoorTime = null;
      
      // 重置AI分析结果到初始状态
      this.$emit('ai-analysis-result', '<div style="text-align: center;"><h2>点击"AI分析"查看AI分析结果</h2></div>');
      
      if (selectedId) {
        console.log(`Filtering data for user ID: ${selectedId}`);
      } else {
        console.log('Showing all users data');
      }
    },
    handleStoreChange(selectedStore) {
      console.log('handleStoreChange:', selectedStore);
      this.selectedStore = selectedStore;
      this.currentRow = null;
      this.currentSelectedRow = null;
      this.selectedOutdoorTime = null;
      
      // 重置AI分析结果到初始状态
      this.$emit('ai-analysis-result', '<div style="text-align: center;"><h2>点击"AI分析"查看AI分析结果</h2></div>');
    },
    handleOptometristChange(selectedOptometrist) {
      console.log('handleOptometristChange:', selectedOptometrist);
      this.selectedOptometrist = selectedOptometrist;
      this.currentRow = null;
      this.currentSelectedRow = null;
      this.selectedOutdoorTime = null;
      
      // 重置AI分析结果到初始状态
      this.$emit('ai-analysis-result', '<div style="text-align: center;"><h2>点击"AI分析"查看AI分析结果</h2></div>');
    },
    handleCheckDateChange(selectedCheckDate) {
      console.log('handleCheckDateChange:', selectedCheckDate);
      this.selectedCheckDate = selectedCheckDate;
      this.currentRow = null;
      this.currentSelectedRow = null;
      this.selectedOutdoorTime = null;
      
      // 重置AI分析结果到初始状态
      this.$emit('ai-analysis-result', '<div style="text-align: center;"><h2>点击"AI分析"查看AI分析结果</h2></div>');
    },
    handleOutdoorTimeChange(selectedTime) {
      console.log('handleOutdoorTimeChange:', selectedTime);
      this.selectedOutdoorTime = selectedTime;
      // 当户外活动日期选择变化时，触发绘制节点
      // this.$emit('on-row-click', this.scores || {});
      this.getUserScores();
    },
    handleResetClick() {
      console.log('handleResetClick');
      this.currentSelectedRow = null;
      this.selectedOutdoorTime = null;
      this.selectedUserId = null;
      this.userIdInput = '';
      
      // 重置AI分析结果到初始状态
      this.$emit('ai-analysis-result', '<div style="text-align: center;"><h2>点击"AI分析"查看AI分析结果</h2></div>');
      
      const urls = `/api/output/`;
      this.$http.get(urls)
        .then(response => {
          this.$message.success(response.data.message || '提交成功');
          this.allTableData = {...response.data.data};
          this.initializeUserIds();
          this.initializeUserNames();
          this.initializeStores();
          this.initializeOptometrists();
          this.initializeCheckDates();
        })
        .catch(error => {
          let errorMessage = '提交失败';
          if (error.response && error.response.data && error.response.data.message) {
            errorMessage = error.response.data.message;
          } else if (error.message) {
            errorMessage = error.message;
          }
          this.$message.error(errorMessage);
          console.error('提交错误:', error.response || error);
        });
    },
    handleAddClick() {
      console.log('handleAddClick');
      if (this.$refs.reusableTable) {
        this.$refs.reusableTable.handleAddDetail();
      }
    },
    handleCurrentRowChange(val) {
      this.currentRow = val;
      this.currentSelectedRow = val;
      this.selectedOutdoorTime = null;
      
      // 重置AI分析结果到初始状态
      this.$emit('ai-analysis-result', '<div style="text-align: center;"><h2>点击"AI分析"查看AI分析结果</h2></div>');
      
      this.$nextTick(() => {
        if (this.outdoorTimes && this.outdoorTimes.length > 0) {
          this.selectedOutdoorTime = this.outdoorTimes[0];
        }
      });
    },
    handleUpdateRow(rowIndex, updatedData, callback) {
      console.log('Updating row:', rowIndex, updatedData);
      this.updateUserData(updatedData, callback);
    },
    handleDeleteRow(index, row) {
      console.log('Delete record:', index, row);
      this.deleteUserData(row.user_id);
    },
    handleRowClick(row, rowIndex, column, event) {
      // eslint-disable-next-line no-unused-vars
      const unusedParams = { column, event };
      console.log('行号:', rowIndex + 1);
      console.log('当前活动的 el-tab-pane name:', this.activeName);
      this.currentSelectedRow = row;
      this.selectedOutdoorTime = null;
      
      // 重置AI分析结果到初始状态
      this.$emit('ai-analysis-result', '<div style="text-align: center;"><h2>点击"AI分析"查看AI分析结果</h2></div>');
      
      this.$nextTick(() => {
        if (this.outdoorTimes && this.outdoorTimes.length > 0) {
          this.selectedOutdoorTime = this.outdoorTimes[0];
        }
        this.getUserScores();
      });
      // this.$emit('on-row-click', this.scores || {});
    },
    updateUserData(data, callback) {
      const urls = `/api/update/`;
      this.$http.post(urls, data, {
        headers: {
          'Content-Type': 'application/json',
        }
      })
        .then(response => {
          this.$message.success(response.data.message || '提交成功');
          console.log('提交成功:', response.data.request_data);
          this.handleResetClick();
          
          // 调用回调函数通知保存成功
          if (callback) {
            callback(true);
          }
        })
        .catch(error => {
          let errorMessage = '提交失败';
          if (error.response && error.response.data && error.response.data.message) {
            errorMessage = error.response.data.message;
          } else if (error.message) {
            errorMessage = error.message;
          }
          this.$message.error(errorMessage);
          console.error('提交错误:', error.response || error);
          
          // 调用回调函数通知保存失败
          if (callback) {
            callback(false);
          }
        });
    },
    deleteUserData(user_id) {
      const urls = `/api/delete/`;
      this.$http.post(urls, { 'user_id': user_id }, {
        headers: {
          'Content-Type': 'application/json',
        }
      })
        .then(response => {
          this.$message.success(response.data.message || '删除成功');
          console.log('删除成功:', response.data.request_data);
          this.handleResetClick();
        })
        .catch(error => {
          let errorMessage = '删除失败';
          if (error.response && error.response.data && error.response.data.message) {
            errorMessage = error.response.data.message;
          } else if (error.message) {
            errorMessage = error.message;
          }
          this.$message.error(errorMessage);
          console.error('删除错误:', error.response || error);
        });
    },
    getUserScores() {
      const url = `/api/score/`;
      if (!this.currentRow) {
        this.$message.error('请先选择一行数据');
        return;
      }
      this.$http.post(url, { user_id: this.currentRow.user_id, check_date: this.selectedOutdoorTime }, {
        headers: {
          'Content-Type': 'application/json',
        }
      })
        .then(response => {
          if (response.data && response.data.data.scores) {
            this.scores = response.data.data.scores;
            console.log('获取用户分数:', this.scores);
            this.$emit('on-row-click', this.scores || {});
          } else {
            console.warn('获取用户分数时，响应数据格式不正确:', response);
          }
        })
        .catch(error => {
          console.error('获取用户分数错误:', error);
        });
    },
    handleAIAnalysis() {
      if (!this.currentRow) {
        this.$message.error('请先选择一行数据');
        return;
      }
      this.isAnalyzing = true;
      const url = `/api/ai/`;
      this.$http.post(url, { 
        user_id: this.currentRow.user_id, 
        check_date: this.selectedOutdoorTime,
        model_name: this.selectedModel
      }, {
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: 120000  // 2分钟超时
      })
        .then(response => {
          if (response.data && response.data.data) {
            this.analysisResult = response.data.data;
            console.log('AI analysis result:', this.analysisResult);
            // 发出事件给父组件，传递分析结果
            this.$emit('ai-analysis-result', this.analysisResult);
          } else {
            this.analysisResult = '未获取到分析结果';
            console.warn('AI analysis response data is not in the expected format:', response);
            this.$emit('ai-analysis-result', this.analysisResult);
          }
        })
        .catch(error => {
          this.analysisResult = 'AI分析失败';
          console.error('Error during AI analysis:', error);
          this.$message.error('AI分析失败');
          this.$emit('ai-analysis-result', this.analysisResult);
        })
        .finally(() => {
          this.isAnalyzing = false;
        });
    },
    handleAIStreamAnalysis() {
      if (!this.currentRow) {
        this.$message.error('请先选择一行数据');
        return;
      }
      
      this.closeStreamConnection();
      this.isStreamAnalyzing = true;
      this.streamCompleted = false;
      this.streamReceivedContent = false;
      this.streamCloseExpected = false;
      this.analysisResult = ''; // 清空之前的结果
      this.$emit('ai-analysis-result', this.analysisResult);
      
      // 构造GET请求的URL参数
      const params = new URLSearchParams({
        user_id: this.currentRow.user_id,
        model_name: this.selectedModel
      });
      
      // 如果有选择日期，添加到参数中
      if (this.selectedOutdoorTime) {
        params.append('check_date', this.selectedOutdoorTime);
      }
      
      const url = `/api/ai_stream/?${params.toString()}`;
      
      // 创建EventSource连接
      this.streamEventSource = new EventSource(url);
      
      // 监听消息事件
      this.streamEventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('Stream data received:', data);
          
          if (data.type === 'data') {
            // 基础数据，可以在这里处理
            console.log('基础数据:', data.scores);
          } else if (data.type === 'content') {
            // 内容片段，实时更新分析结果
            this.streamReceivedContent = true;
            this.analysisResult += data.content;
            // 发出事件给父组件，传递实时更新的分析结果
            console.log('得到输出:', data.content);
            this.$emit('ai-analysis-result', this.analysisResult);
          } else if (data.type === 'complete') {
            // 流式输出完成
            console.log('流式分析完成');
            this.streamCompleted = true;
            this.analysisResult = data.full_content || this.analysisResult;
            this.$emit('ai-analysis-result', this.analysisResult);
            this.closeStreamConnection();
            this.$message.success('AI分析完成');
            console.log('完整输出:', data.full_content);
          } else if (data.type === 'error') {
            // 错误处理
            console.error('流式分析错误:', data.error);
            this.$message.error(`流式分析失败: ${data.error}`);
            this.closeStreamConnection();
          }
        } catch (error) {
          console.error('解析流式数据时出错:', error);
          console.log('原始数据:', event.data);
        }
      };
      
      // 监听错误事件
      this.streamEventSource.onerror = (error) => {
        console.error('EventSource连接错误:', error);
        if (this.streamCloseExpected || this.streamCompleted) {
          this.closeStreamConnection();
          return;
        }
        if (this.streamReceivedContent) {
          console.warn('Stream closed after receiving content; preserving partial result');
          this.closeStreamConnection();
          return;
        }
        this.$message.error('流式分析连接失败');
        this.closeStreamConnection();
      };
      
      // 监听连接打开事件
      this.streamEventSource.onopen = () => {
        console.log('EventSource连接已建立');
      };
    },
    closeStreamConnection() {
      if (this.streamEventSource) {
        this.streamCloseExpected = true;
        this.streamEventSource.onmessage = null;
        this.streamEventSource.onerror = null;
        this.streamEventSource.onopen = null;
        this.streamEventSource.close();
        this.streamEventSource = null;
      }
      this.isStreamAnalyzing = false;
    },
    // 分析批注相关方法
    handleAnnotationClick() {
      if (!this.currentRow) {
        this.$message.error('请先选择一行数据');
        return;
      }
      
      this.annotationDialogVisible = true;
      this.annotationInput = '';
      
      // 直接使用当前的AI分析结果
      if (this.$parent.aiAnalysisResult && this.$parent.aiAnalysisResult.trim() !== '<div style="text-align: center;"><h2>点击"AI分析"查看AI分析结果</h2></div>') {
        this.annotationAIResult = this.$parent.aiAnalysisResult;
        
        // 获取已保存的批注内容
        this.getExistingAnnotation(this.$parent.aiAnalysisResult);
      } else {
        this.annotationAIResult = '<div style="text-align: center; color: #909399;"><p>暂无AI分析结果，请先进行AI分析</p></div>';
      }
    },
    // 获取已保存的批注内容
    getExistingAnnotation(aiAnalysisContent) {
      const url = `/api/get_annotation/`;
      const requestData = {
        ai_analysis_content: aiAnalysisContent
      };
      
      this.$http.post(url, requestData, {
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: 10000
      })
        .then(response => {
          if (response.data && response.data.status === 'success' && response.data.data) {
            if (response.data.data.annotation_content) {
              // 找到已保存的批注，填充到输入框
              this.annotationInput = response.data.data.annotation_content;
              console.log('已找到保存的批注:', response.data.data.annotation_content);
            } else {
              // 未找到已保存的批注
              console.log('未找到已保存的批注');
            }
          }
        })
        .catch(error => {
          console.error('获取批注错误:', error);
          // 获取批注失败不影响弹窗打开，只是不填充内容
        });
    },
    handleAnnotationDialogClose() {
      this.annotationInput = '';
      this.annotationAIResult = '<div style="text-align: center; color: #909399;"><p>正在获取当前AI分析结果...</p></div>';
      this.isAnnotationSubmitting = false;
    },
    handleAnnotationSubmit() {
      if (!this.annotationInput.trim()) {
        this.$message.warning('请输入批注内容');
        return;
      }
      
      // 检查是否有AI分析结果
      if (!this.annotationAIResult || this.annotationAIResult.includes('暂无AI分析结果') || this.annotationAIResult.includes('正在获取当前AI分析结果')) {
        this.$message.warning('请先进行AI分析后再添加批注');
        return;
      }
      
      this.isAnnotationSubmitting = true;
      
      const url = `/api/annotation/`;
      const requestData = {
        user_id: this.currentRow.user_id,
        ai_analysis_content: this.annotationAIResult,
        annotation_content: this.annotationInput.trim(),
        model_name: this.selectedModel
      };
      
      this.$http.post(url, requestData, {
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: 120000  // 2分钟超时
      })
        .then(response => {
          if (response.data && response.data.status === 'success') {
            this.$message.success(response.data.message || '批注保存成功');
            this.annotationDialogVisible = false;
          } else {
            this.$message.error('批注保存失败：' + (response.data.message || '未知错误'));
          }
        })
        .catch(error => {
          console.error('批注保存错误:', error);
          this.$message.error('批注保存失败：' + (error.response?.data?.message || '网络错误'));
        })
        .finally(() => {
          this.isAnnotationSubmitting = false;
        });
    },
    getDownloadFileName(disposition, startDate, endDate) {
      if (disposition) {
        const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
        if (utf8Match && utf8Match[1]) {
          return decodeURIComponent(utf8Match[1]);
        }
        const filenameMatch = disposition.match(/filename="?([^"]+)"?/i);
        if (filenameMatch && filenameMatch[1]) {
          return filenameMatch[1];
        }
      }
      return `AI产品建议_${startDate}_${endDate}.xlsx`;
    },
    readApiErrorMessage(error) {
      return error.response?.data?.message || error.message || '网络错误';
    },
    async readBlobErrorMessage(error) {
      if (!error.response || !(error.response.data instanceof Blob)) {
        return this.readApiErrorMessage(error);
      }
      const text = await error.response.data.text();
      try {
        const payload = JSON.parse(text);
        return payload.message || payload.error || text || '导出失败';
      } catch (e) {
        return text || '导出失败';
      }
    },
    clearExportPollTimer() {
      if (this.exportPollTimer) {
        clearTimeout(this.exportPollTimer);
        this.exportPollTimer = null;
      }
    },
    waitForExportPoll(interval = 2000) {
      this.clearExportPollTimer();
      return new Promise(resolve => {
        this.exportPollTimer = setTimeout(() => {
          this.exportPollTimer = null;
          resolve();
        }, interval);
      });
    },
    async pollExportJobStatus(jobId) {
      let isPending = true;
      while (isPending) {
        const response = await this.$http.get(`/api/export_ai_product_suggestions/${jobId}/`, {
          timeout: 15000
        });
        const job = response.data?.data || {};

        if (job.status === 'success') {
          return job;
        }
        if (job.status === 'failed') {
          throw new Error(job.error_message || '导出失败');
        }

        isPending = job.status === 'pending' || job.status === 'running';
        await this.waitForExportPoll();
      }

      throw new Error('导出任务状态异常');
    },
    async downloadExportJob(job, startDate, endDate) {
      const response = await this.$http.get(`/api/export_ai_product_suggestions/${job.id}/download/`, {
        responseType: 'blob',
        timeout: 120000
      });

      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const filename = this.getDownloadFileName(
        response.headers['content-disposition'],
        startDate,
        endDate
      );
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || job.file_name || `AI产品建议_${startDate}_${endDate}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    async handleExportAiSuggestions() {
      if (!this.exportDateRange || this.exportDateRange.length !== 2) {
        this.$message.warning('请先选择导出日期范围');
        return;
      }

      const [startDate, endDate] = this.exportDateRange;
      this.isExporting = true;

      try {
        const createResponse = await this.$http.post('/api/export_ai_product_suggestions/', {
          start_date: startDate,
          end_date: endDate,
          model_name: this.selectedModel
        }, {
          timeout: 15000
        });
        const job = createResponse.data?.data;
        if (!job || !job.id) {
          throw new Error('导出任务创建失败');
        }

        const completedJob = await this.pollExportJobStatus(job.id);
        await this.downloadExportJob(completedJob, startDate, endDate);
        this.$message.success('导出完成');
      } catch (error) {
        const message = error.response ? await this.readBlobErrorMessage(error) : (error.message || '导出失败');
        this.$message.error(`导出失败：${message}`);
        console.error('导出AI产品建议失败:', error);
      } finally {
        this.clearExportPollTimer();
        this.isExporting = false;
      }
    },
    handleToolbarCommand(command) {
      if (command === 'print') {
        this.triggerHiddenPrint();
        return;
      }
      if (command === 'annotation') {
        this.handleAnnotationClick();
        return;
      }
      if (command === 'cache') {
        this.handleAiCacheClick();
      }
    },
    triggerHiddenPrint() {
      if (this.isStreamAnalyzing) {
        return;
      }
      const buttonRef = this.$refs.hiddenPrintButton;
      if (buttonRef && buttonRef.$el && typeof buttonRef.$el.click === 'function') {
        buttonRef.$el.click();
        return;
      }
      if (buttonRef && typeof buttonRef.click === 'function') {
        buttonRef.click();
      }
    },
    handleAiCacheClick() {
      this.aiCacheDialogVisible = true;
    }
  },
  beforeDestroy() {
    // 组件销毁前清理EventSource连接
    this.closeStreamConnection();
    this.clearExportPollTimer();
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.eye-chart {
  display: flex;
  flex-direction: column;
  align-items: left;
  justify-content: flex-start;
  height: 100%;
  gap: 10px;
  padding: 0;
}

.filter-container {
  width: 100%;
  padding-left: 1px;
}

.toolbar-panel {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 10px 0;
}

.toolbar-filters {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.toolbar-row {
  display: grid;
  align-items: center;
  gap: 10px 14px;
}

.toolbar-row--primary {
  grid-template-columns: repeat(5, minmax(0, 1fr));
}

.toolbar-row--secondary {
  grid-template-columns: minmax(0, 1.35fr) minmax(0, 0.9fr) minmax(0, 1.75fr);
}

.toolbar-field {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.toolbar-field--wide {
  min-width: 0;
}

.toolbar-label {
  flex: 0 0 auto;
  min-width: 74px;
  font-size: 14px;
  color: #1f2937;
  white-space: nowrap;
}

.toolbar-control {
  flex: 1 1 auto;
  min-width: 0;
  width: 100%;
}

.toolbar-control ::v-deep .el-input__inner,
.toolbar-control ::v-deep .el-range-input,
.toolbar-control ::v-deep .el-range-separator,
.toolbar-control ::v-deep .el-input__icon {
  font-size: 14px;
}

.toolbar-control ::v-deep .el-date-editor,
.toolbar-control ::v-deep .el-date-editor.el-input,
.toolbar-control ::v-deep .el-date-editor.el-input__inner,
.toolbar-control ::v-deep .el-select,
.toolbar-control ::v-deep .el-input {
  width: 100%;
}

.toolbar-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: nowrap;
  min-width: 0;
}

.toolbar-button,
.toolbar-dropdown {
  flex: 0 0 auto;
}

.toolbar-button {
  margin: 0;
  font-size: 14px;
  white-space: nowrap;
}

.toolbar-hidden-print {
  position: absolute;
  left: -9999px;
  top: 0;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: 0;
  border: 0;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
}

::v-deep .el-table th {
  padding: 6px 0;
}

::v-deep .el-table td {
  padding: 6px 0;
}

.el-table button {
  font-size: 14px;
}

::v-deep .el-tabs__content {
  padding: 0 15px 0 15px;
}

.pagination-right {
  text-align: right;
}

::v-deep .el-textarea__inner {
  width: 100%;
  font-size: 14px;
  border-color: #DCDFE6;
  border-width: 1px;
  border-radius: 20px;
}

/* 分析批注弹窗样式 */
.annotation-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
  height: 500px;
}

.annotation-input-section {
  flex-shrink: 0;
}

.annotation-ai-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.ai-analysis-area {
  flex: 1;
  border: 1px solid #DCDFE6;
  border-radius: 4px;
  min-height: 300px;
}

.dialog-footer {
  text-align: right;
}

::v-deep .el-dialog__body {
  padding: 20px;
}

@media (max-width: 1280px) {
  .toolbar-row--primary {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .toolbar-row--secondary {
    grid-template-columns: minmax(0, 1fr) minmax(0, 0.85fr) minmax(0, 1.4fr);
  }
}
</style>
